/**
 * The one time caption a visit card carries, on My week and on My day alike.
 *
 * "2:00-3:30PM arrival", or "1400-1530 arrival" for somebody who has the
 * military clock on — the arrival window the patient actually has, phrased
 * through the product's **own** helper (`timeRangeLabel`) and the user's **own**
 * setting (the military-time switch in the user menu).
 *
 * A card with no window — a block, a hold, a reservation, none of which the
 * patient was ever told a window for — reads its own span instead, and one the
 * placement search could not fit reads **Needs a time**, because it has none. `start` and
 * `end` stay the branch-zone `"HH:mm"` **values** the engine sorts on and the
 * day axis measures in; only their phrasing follows the setting.
 *
 * It lives in the view layer because that is the only place the preference can
 * be read: `selectMyWeek` is a pure selector and React context is not available
 * to it. Everything else the demo shows a clock time for goes the same way now,
 * through `useBranchClock`.
 */
import type { WeekCard } from "../engine/myWeek";
import { useBranchClock } from "./branchClock";

export type CardWindow = Pick<WeekCard, "start" | "end" | "arrivalWindowStartIso" | "arrivalWindowEndIso">;

export function useCardWindowLabel(timeZone: string): (card: CardWindow) => string {
  const clock = useBranchClock(timeZone);
  return (card) => {
    if (card.arrivalWindowStartIso && card.arrivalWindowEndIso) return `${clock.range(card.arrivalWindowStartIso, card.arrivalWindowEndIso)} arrival`;
    // A reservation the placement search could not fit has no hour to phrase.
    if (!card.start) return "Needs a time";
    return `${clock.wall(card.start)}${card.end && card.end !== card.start ? `–${clock.wall(card.end)}` : ""}`;
  };
}
